import React, { useEffect, useState } from 'react';
import { GoogleMap, OverlayView } from '@react-google-maps/api';
import Loader from 'components/atoms/loader';
import { useBiddingPlaces } from 'hooks/useBiddingPlaces';
import { useGoogleMaps } from 'hooks/useGoogleMaps';
import { PossibleCompetitorBidding } from 'services/actions/ai_management_possible_competitors';
import { Frame } from './styles';

interface AiManagementCompetitorBiddingsMapProps {
  biddings: PossibleCompetitorBidding[];
  onOpen: (bidding: PossibleCompetitorBidding) => void;
}

const BRAZIL_CENTER = { lat: -14.235, lng: -51.925 };
const CONTAINER_STYLE = { width: '100%', height: '100%' };
const MAP_OPTIONS = { streetViewControl: false, mapTypeControl: false, fullscreenControl: false };
const SINGLE_PLACE_ZOOM = 9;
const BOUNDS_PADDING = 64;

const labelOffset = (width: number, height: number) => ({ x: -width / 2, y: -height - 6 });

const AiManagementCompetitorBiddingsMap: React.FC<AiManagementCompetitorBiddingsMapProps> = ({
  biddings,
  onOpen,
}) => {
  const { isLoaded } = useGoogleMaps();
  const { places, missingCount, resolving } = useBiddingPlaces(biddings, isLoaded);
  const [map, setMap] = useState<google.maps.Map | null>(null);

  useEffect(() => {
    if (!map || !places.length) return;
    if (places.length === 1) {
      map.setCenter(places[0].position);
      map.setZoom(SINGLE_PLACE_ZOOM);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    places.forEach((place) => bounds.extend(place.position));
    map.fitBounds(bounds, BOUNDS_PADDING);
  }, [map, places]);

  if (!isLoaded) {
    return (
      <div className="py-5">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <Frame className="position-relative overflow-hidden">
        <GoogleMap
          mapContainerStyle={CONTAINER_STYLE}
          center={BRAZIL_CENTER}
          zoom={4}
          options={MAP_OPTIONS}
          onLoad={setMap}
        >
          {places.map((place) => (
            <OverlayView
              key={place.key}
              position={place.position}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              getPixelPositionOffset={labelOffset}
            >
              <div
                className="map-label position-relative d-flex flex-column rounded shadow"
                title={place.key}
              >
                {place.biddings.map((bidding) => (
                  <button
                    key={bidding.bidding_id}
                    type="button"
                    className="badge map-label-item border-0 text-white"
                    onClick={() => onOpen(bidding)}
                  >
                    {bidding.edital ?? `Nº ${bidding.bidding_id}`}
                  </button>
                ))}
              </div>
            </OverlayView>
          ))}
        </GoogleMap>
        {resolving ? (
          <div className="map-loading position-absolute d-flex align-items-center justify-content-center">
            <Loader />
          </div>
        ) : null}
      </Frame>
      {!resolving && missingCount ? (
        <div className="small text-muted mt-2">
          {missingCount === 1
            ? '1 licitação sem cidade localizada não aparece no mapa.'
            : `${missingCount} licitações sem cidade localizada não aparecem no mapa.`}
        </div>
      ) : null}
    </>
  );
};

export default AiManagementCompetitorBiddingsMap;
